import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar, MapPin, Users, Clock, Plus, Search, Download, ExternalLink, Globe, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useTranslation } from '../i18n';

const categoryLabels = {
  trade_show: 'Trade Show', webinar: 'Webinar', networking: 'Networking',
  conference: 'Conference', workshop: 'Workshop'
};
const typeLabels = { in_person: 'In Person', virtual: 'Virtual', hybrid: 'Hybrid' };
const categoryColors = {
  trade_show: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  webinar: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  networking: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  conference: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  workshop: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
};

export default function EventsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '', description: '', category: 'trade_show', type: 'in_person',
    location: '', start_date: '', end_date: '', timezone: 'UTC', max_attendees: '',
    industries: [], external_url: ''
  });

  const { data, isLoading } = useQuery({
    queryKey: ['events', search, category, page],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category && category !== 'all') params.set('category', category);
      params.set('page', String(page));
      params.set('limit', '12');
      return api.get(`/events?${params}`).then(r => r.data);
    }
  });

  const rsvpMutation = useMutation({
    mutationFn: ({ eventId, cancel }) => cancel
      ? api.delete(`/events/${eventId}/rsvp`)
      : api.post(`/events/${eventId}/rsvp`),
    onSuccess: () => queryClient.invalidateQueries(['events'])
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/events', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['events']);
      setShowCreate(false);
      setNewEvent({ title: '', description: '', category: 'trade_show', type: 'in_person', location: '', start_date: '', end_date: '', timezone: 'UTC', max_attendees: '', industries: [], external_url: '' });
    }
  });

  const handleCreate = () => {
    const payload = { ...newEvent };
    if (payload.max_attendees) payload.max_attendees = Number(payload.max_attendees);
    else delete payload.max_attendees;
    if (!payload.end_date) delete payload.end_date;
    if (!payload.external_url) delete payload.external_url;
    createMutation.mutate(payload);
  };

  const events = data?.events || [];
  const totalPages = data?.pages || 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("events.events")}</h1>
          <p className="text-sm text-muted-foreground">{t("events.tradeShowsWebinarsAnd")}</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" /> {t("events.createEvent") || "Create Event"}</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t("events.createEvent")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t("events.title")}</Label>
                <Input value={newEvent.title} onChange={e => setNewEvent(p => ({...p, title: e.target.value}))} placeholder={t("events.ph.title") || "Event title"} />
              </div>
              <div className="space-y-2">
                <Label>{t("events.description")}</Label>
                <Textarea value={newEvent.description} onChange={e => setNewEvent(p => ({...p, description: e.target.value}))} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("events.category")}</Label>
                  <Select value={newEvent.category} onValueChange={v => setNewEvent(p => ({...p, category: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(categoryLabels).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("events.type")}</Label>
                  <Select value={newEvent.type} onValueChange={v => setNewEvent(p => ({...p, type: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(typeLabels).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("events.locationUrl")}</Label>
                <Input value={newEvent.location} onChange={e => setNewEvent(p => ({...p, location: e.target.value}))} placeholder={t("events.ph.location") || "Venue address or meeting URL"} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("events.startDateTime")}</Label>
                  <Input type="datetime-local" value={newEvent.start_date} onChange={e => setNewEvent(p => ({...p, start_date: e.target.value}))} />
                </div>
                <div className="space-y-2">
                  <Label>{t("events.endDateTime")}</Label>
                  <Input type="datetime-local" value={newEvent.end_date} onChange={e => setNewEvent(p => ({...p, end_date: e.target.value}))} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("events.maxAttendeesOptional")}</Label>
                <Input type="number" value={newEvent.max_attendees} onChange={e => setNewEvent(p => ({...p, max_attendees: e.target.value}))} placeholder={t("events.ph.maxAttendees") || "Leave empty for unlimited"} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>{t("events.cancel")}</Button>
              <Button onClick={handleCreate} disabled={!newEvent.title || !newEvent.start_date || createMutation.isLoading}>
                {createMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t("events.createEvent") || "Create Event"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder={t("events.searchPlaceholder") || "Search events..."} value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <Select value={category} onValueChange={v => { setCategory(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder={t("events.allCategories") || "All Categories"} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("events.allCategories")}</SelectItem>
            {Object.entries(categoryLabels).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Events Grid */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : events.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">{t("events.noEventsFoundCreate")}</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map(event => (
            <Card key={event._id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">{event.title}</CardTitle>
                    {event.organizer_company && (
                      <CardDescription className="text-xs mt-1">by {event.organizer_company}</CardDescription>
                    )}
                  </div>
                  {event.is_featured && <Badge variant="outline" className="shrink-0 text-xs">{t("events.featured")}</Badge>}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-1.5">
                  <Badge className={`text-xs ${categoryColors[event.category] || 'bg-muted'}`}>
                    {categoryLabels[event.category] || event.category}
                  </Badge>
                  <Badge variant="outline" className="text-xs gap-1">
                    <Globe className="h-3 w-3" /> {typeLabels[event.type] || event.type}
                  </Badge>
                </div>

                <div className="space-y-1.5 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-3.5 w-3.5 shrink-0" />
                    <span>{new Date(event.start_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 shrink-0" />
                    <span>{new Date(event.start_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  {event.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{event.location}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Users className="h-3.5 w-3.5 shrink-0" />
                    <span>{event.rsvp_count || 0} attending{event.max_attendees ? ` / ${event.max_attendees} max` : ''}</span>
                  </div>
                </div>

                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    variant={event.user_has_rsvp ? "outline" : "default"}
                    className="flex-1"
                    onClick={() => rsvpMutation.mutate({ eventId: event._id, cancel: event.user_has_rsvp })}
                    disabled={rsvpMutation.isLoading}
                  >
                    {event.user_has_rsvp ? t('events.cancelRsvp') || 'Cancel RSVP' : t('events.rsvp') || 'RSVP'}
                  </Button>
                  <Button size="sm" variant="outline" asChild>
                    <a href={`${api.defaults.baseURL}/events/${event._id}/ics`} download>
                      <Download className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                  {event.external_url && (
                    <Button size="sm" variant="outline" asChild>
                      <a href={event.external_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>{t("events.previous")}</Button>
          <span className="text-sm text-muted-foreground self-center">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{t("events.next")}</Button>
        </div>
      )}
    </div>
  );
}
